/**
 * ТЗ 0076 — договоры с подписанием через Documenso. Словари и чистые помощники.
 *
 * Статусы на проводе — строчные строки (`draft`, `send_failed`), а не
 * graphene-enum'ы: сравнивать только с константами отсюда, не с литералом,
 * набранным руками в компоненте.
 *
 * Модуль без React, Apollo и i18n: подписи — ключи `esign.*`, их переводит
 * вызывающий.
 */

export const ESIGN_STATUSES = [
  'draft',
  'sending',
  'send_failed',
  'sent',
  'opened',
  'completed',
  'rejected',
  'cancelled',
  'expired',
] as const;

export type EsignStatus = typeof ESIGN_STATUSES[number];

export const ESIGN_FULFILMENT_STATUSES = [
  'not_applicable',
  'pending',
  'done',
  'failed',
] as const;

export const ESIGN_KINDS = [
  'student',
  'company',
  'teacher_framework',
  'teacher_annex',
] as const;

export type EsignKind = typeof ESIGN_KINDS[number];

/** Договор студента — единственный вид с SMS-напоминаниями (ТЗ 0077). */
export const ESIGN_STUDENT_KIND = 'student';

/**
 * Договор с фирмой-клиентом (B2B). Права — как у договора студента; после
 * подписи ERP только хранит PDF и статус (участников не зачисляет).
 */
export const ESIGN_COMPANY_KIND = 'company';

/**
 * «Продажные» виды (ТЗ 0078): у них есть продавец и переписка с подписантом
 * из карточки. Зеркало `SALES_KINDS` бэкенда (`esign/constants.py`).
 */
export const ESIGN_SALES_KINDS: readonly string[] = [
  ESIGN_STUDENT_KIND,
  ESIGN_COMPANY_KIND,
];

export const isEsignSalesKind = (kind: string): boolean =>
  ESIGN_SALES_KINDS.includes(kind);

/**
 * `inviteBlockedReason` карточки: письмо-приглашение из ящика не уйдёт — у
 * продавца нет подключённого ящика и общего запасного тоже нет. Бэкенд тогда
 * отвечает на отправку письма `NO_MAILBOX`, поэтому кнопки выключены заранее.
 */
export const ESIGN_INVITE_NO_MAILBOX = 'no_mailbox';

/**
 * ТЗ 0084: ссылка в письме-приглашении — только короткая. Сократитель не
 * настроен на сервере — письмо не уйдёт (бэкенд откажет
 * `SHORTLINK_DISABLED`), кнопки письма выключены заранее.
 */
export const ESIGN_INVITE_SHORTLINK_DISABLED = 'shortlink_disabled';

/** Причины `inviteBlockedReason`, при которых письмо-приглашение не уйдёт. */
export const ESIGN_INVITE_BLOCKED_REASONS: readonly string[] = [
  ESIGN_INVITE_NO_MAILBOX,
  ESIGN_INVITE_SHORTLINK_DISABLED,
];

/** Модель оплаты B2B; первая — по умолчанию (KFS/BUR — самый частый случай). */
export const ESIGN_PAYMENT_MODELS = ['postpayment', 'prepayment'] as const;

/** На кого фактура при postpłacie — две галочки Zamówienia. */
export const ESIGN_INVOICE_TARGETS = ['customer', 'funder'] as const;

export const ESIGN_TEACHER_KINDS: readonly string[] = [
  'teacher_framework',
  'teacher_annex',
];

/** Шаблоны рамочного договора, которые HR может выбрать руками. */
export const ESIGN_FRAMEWORK_TEMPLATES = [
  'teacher_framework_zlecenie',
  'teacher_framework_b2b',
] as const;

/** Рамочный договор преподавателя — вид документа на проводе. */
export const ESIGN_TEACHER_FRAMEWORK_KIND = 'teacher_framework';

/**
 * Статусы документа преподавателя, при которых второй такой же не создаётся
 * (перепроверка 0079, как на сервере `_refuse_live_teacher_doc`): оформляется,
 * истёк (ссылка у Documenso жива до отмены) или подписан. Отклонённый и
 * отменённый — не мешают.
 */
export const ESIGN_TEACHER_BLOCKING_STATUSES: readonly string[] = [
  'draft',
  'sending',
  'send_failed',
  'sent',
  'opened',
  'expired',
  'completed',
];

interface IEsignTeacherDocRow {
  kind: string;
  status: string;
  fulfilmentStatus: string;
}

/**
 * Рамочный договор, из-за которого сервер второй не создаст: ещё
 * подписывается, истёк с живой ссылкой, или подписан, но не оформлен
 * («Повторить» — в карточке документа, ревью 0079). Подписанный и
 * оформленный сюда не входит — это «действующий договор».
 *
 * Одно правило на карточку преподавателя и окно «Договор с преподавателем».
 */
export const esignLiveTeacherFramework = <T extends IEsignTeacherDocRow>(
  rows: readonly T[],
): T | undefined =>
  rows.find(
    (row) =>
      row.kind === ESIGN_TEACHER_FRAMEWORK_KIND &&
      ESIGN_TEACHER_BLOCKING_STATUSES.includes(row.status) &&
      (row.status !== 'completed' || row.fulfilmentStatus !== 'done'),
  );

/** Подписанный и оформленный рамочный договор — на него ведёт «Открыть договор». */
export const esignSignedTeacherFramework = <T extends IEsignTeacherDocRow>(
  rows: readonly T[],
): T | undefined =>
  rows.find(
    (row) =>
      row.kind === ESIGN_TEACHER_FRAMEWORK_KIND &&
      row.status === 'completed' &&
      row.fulfilmentStatus === 'done',
  );

/**
 * Почему живой рамочный договор мешает новому — тем же текстом, каким
 * откажет сервер (`esign.errors.<CODE>`): подписан, но не оформлен; истёк со
 * ссылкой, которая ещё действует; иначе — оформляется.
 */
export const esignLiveFrameworkReasonKey = (status: string): string => {
  if (status === 'completed') return 'esign.errors.TEACHER_SIGNED_PENDING';
  if (status === 'expired') return 'esign.errors.TEACHER_DOC_EXPIRED_LIVE';
  return 'esign.errors.TEACHER_DOC_IN_PROGRESS';
};

/**
 * Что показать в окне «Договор с преподавателем» для выбранного человека.
 *
 * Каждое состояние, кроме `ready`, — отказ сервера
 * (`create_teacher_framework_draft`), показанный ДО клика. Живой документ —
 * первым, как на карточке преподавателя: человеку нужна ссылка на него, а
 * не только «нельзя». Email сервер проверяет при сборке документа, но без
 * него откажет всегда — поэтому и он до «можно».
 *
 * * `checking` — договоры преподавателя ещё не прочитаны;
 * * `hasAgreement` — действует договор (`isDismissed === false`);
 * * `inProgress` — живой рамочный документ (`esignLiveTeacherFramework`);
 * * `noEmail` — ссылку на подпись некуда адресовать;
 * * `ready` — можно создавать.
 */
export type EsignTeacherFrameworkState =
  | 'checking'
  | 'hasAgreement'
  | 'inProgress'
  | 'noEmail'
  | 'ready';

export const esignTeacherFrameworkState = ({
  checking,
  isDismissed,
  hasLiveFramework,
  email,
}: {
  checking: boolean;
  isDismissed: boolean | null | undefined;
  hasLiveFramework: boolean;
  email: string | null | undefined;
}): EsignTeacherFrameworkState => {
  if (checking) return 'checking';
  if (hasLiveFramework) return 'inProgress';
  if (isDismissed === false) return 'hasAgreement';
  if (!(email ?? '').trim()) return 'noEmail';
  return 'ready';
};

/**
 * Отказы создания документа преподавателя (перепроверка 0079). Переводятся
 * `esign.errors.<CODE>`.
 */
export const ESIGN_TEACHER_ERROR_CODES = [
  'TEACHER_HAS_AGREEMENT',
  'TEACHER_SIGNED_PENDING',
  'TEACHER_DOC_IN_PROGRESS',
  'TEACHER_DOC_EXPIRED_LIVE',
  // Приложение к этой заявке уже подписано.
  'TEACHER_ANNEX_SIGNED',
] as const;

/**
 * Карточка занятости, которую ERP заводит из подписанной формы рамочного
 * договора (`teacher_employment_created.details.model`).
 */
export const ESIGN_TEACHER_EMPLOYMENT_MODELS = [
  'NaturalPerson',
  'LegalEntity',
] as const;

/**
 * Почему карточка занятости не заведена
 * (`teacher_employment_skipped.details.reason`). Прочие коды — общей фразой
 * `unknown`: бэкенд может добавить новый раньше фронта.
 */
export const ESIGN_TEACHER_EMPLOYMENT_SKIP_REASONS = [
  'citizenship_not_found',
  'b2b_incomplete',
  'zlecenie_incomplete',
] as const;

/** Ключ перевода причины «карточка занятости не заведена». */
export const esignEmploymentSkipKey = (reason: string): string =>
  (ESIGN_TEACHER_EMPLOYMENT_SKIP_REASONS as readonly string[]).includes(reason)
    ? `esign.employmentSkip.${reason}`
    : 'esign.employmentSkip.unknown';

/** Зеркало общего `TagTone` из `_detailShared/detail.styled`. */
export type EsignTagTone =
  | 'ok'
  | 'warn'
  | 'danger'
  | 'neutral'
  | 'info'
  | 'gold';

/**
 * Тон плашки статуса. Правило одно на все экраны (список, ящик, карточки
 * студента, группы и преподавателя) — поэтому здесь, а не в каждом из них.
 */
export const esignStatusTone = (status: string): EsignTagTone => {
  switch (status) {
    case 'completed':
      return 'ok';
    case 'sending':
    case 'sent':
      return 'info';
    case 'opened':
      return 'gold';
    case 'send_failed':
    case 'rejected':
      return 'danger';
    default:
      return 'neutral';
  }
};

/**
 * Плашка «зачисление» нужна только когда о нём есть что сказать: ждёт или
 * упало. `done` и `not_applicable` — норма, и чип на каждой строке был бы
 * шумом.
 */
export const esignFulfilmentTone = (
  fulfilmentStatus: string,
): EsignTagTone | null => {
  if (fulfilmentStatus === 'failed') return 'danger';
  if (fulfilmentStatus === 'pending') return 'warn';
  return null;
};

/**
 * Идёт ли у документа процесс, конец которого увидит только опрос: отправка
 * в Documenso (Celery) или зачисление после подписи.
 */
export const isEsignInFlight = (row: {
  status: string;
  fulfilmentStatus: string;
}): boolean =>
  row.status === 'sending' ||
  (row.status === 'completed' && row.fulfilmentStatus === 'pending');

/** Сколько карточка ждёт письмо после запроса, прежде чем бросить опрос. */
export const ESIGN_EMAIL_WAIT_MS = 2 * 60 * 1000;

const EMAIL_OUTCOMES = ['email_sent', 'email_failed', 'email_skipped'];

/**
 * Письмо попросили (отправка с письмом или «Отправить письмо повторно»), а
 * исхода ещё нет. Статус «Отправлен» ставится ДО письма, и опрос по одному
 * статусу останавливался раньше, чем письмо уходило: карточка так и
 * показывала «письма не было». Ждём не дольше `ESIGN_EMAIL_WAIT_MS` — если
 * воркер лежит, опрос не должен идти вечно.
 */
export const isEsignEmailPending = (
  row: {
    status: string;
    events: ReadonlyArray<{
      event: string;
      occurredAt: string;
      details?: unknown;
    }>;
  },
  now: number = Date.now(),
): boolean => {
  if (!['sending', 'sent', 'opened'].includes(row.status)) return false;
  for (let i = row.events.length - 1; i >= 0; i -= 1) {
    const item = row.events[i];
    if (EMAIL_OUTCOMES.includes(item.event)) return false;
    const asked =
      item.event === 'email_resend_requested' ||
      (item.event === 'send_requested' &&
        Boolean(
          item.details && (item.details as { send_email?: unknown }).send_email,
        ));
    if (item.event === 'send_requested' || asked) {
      return (
        asked && now - new Date(item.occurredAt).getTime() < ESIGN_EMAIL_WAIT_MS
      );
    }
  }
  return false;
};

/** Из каких статусов бэкенд принимает `sendContract` (SENDABLE_STATUSES). */
export const isEsignSendable = (status: string): boolean =>
  status === 'draft' || status === 'send_failed';

/** Когда ссылку можно раскрыть (`reveal_link`: sent | opened). */
export const isEsignLinkReady = (status: string): boolean =>
  status === 'sent' || status === 'opened';

/**
 * Договор подписан: `completed` или уже есть время подписи — событие
 * Documenso «подписано» ставит `signedAt` раньше, чем придёт `completed`
 * (статус в этом окне ещё `sent`/`opened`). Подписанту можно написать SMS
 * вручную, но без `{link}`: ссылка на подпись после подписи мертва, сервер
 * откажет `LINK_NOT_READY`. Лестница напоминаний по нему не ходит.
 * Копия `esign/outreach_topic.py::_is_signed`: отменённый, отклонённый,
 * истёкший и «подписан после отмены» (`cancelConflict`) — не подписанные.
 */
export const isEsignSigned = (row: {
  status: string;
  signedAt?: unknown;
  cancelConflict?: boolean | null;
}): boolean =>
  !row.cancelConflict &&
  (row.status === 'completed' ||
    (isEsignLinkReady(row.status) && Boolean(row.signedAt)));

/** CANCELLABLE_STATUSES бэкенда. */
export const isEsignCancellable = (status: string): boolean =>
  ['draft', 'sending', 'send_failed', 'sent', 'opened', 'expired'].includes(
    status,
  );

/**
 * События журнала, у которых есть человеческая подпись (`esign.events.*`).
 * Неизвестное событие показывается своим кодом — бэкенд может добавить новое
 * раньше, чем фронт узнает о нём, и терять строку журнала нельзя.
 */
export const ESIGN_KNOWN_EVENTS = [
  'draft_created',
  'draft_updated',
  'send_requested',
  'sent',
  'send_failed',
  'email_sent',
  'email_skipped',
  'email_failed',
  // Копия подписанного договора: её шлёт ERP из общего ящика (Documenso
  // писем не шлёт с 2026-09-28).
  'signed_copy_sent',
  'signed_copy_skipped',
  'signed_copy_failed',
  'email_resend_requested',
  // События SendGrid по письму со ссылкой.
  'delivered',
  'open',
  'bounce',
  'dropped',
  'link_revealed',
  'DOCUMENT_SENT',
  'DOCUMENT_OPENED',
  'DOCUMENT_SIGNED',
  'DOCUMENT_RECIPIENT_COMPLETED',
  'DOCUMENT_COMPLETED',
  'DOCUMENT_REJECTED',
  'DOCUMENT_CANCELLED',
  'RECIPIENT_EXPIRED',
  'finalized',
  'enrolled',
  'fulfilment_failed',
  'fulfilment_retry',
  'cancelled',
  'teacher_agreement_created',
  // ТЗ 0079: введённое при подписи дописано в карточку преподавателя.
  'teacher_card_updated',
  'teacher_card_update_failed',
  // Карточка занятости преподавателя заведена из подписанной формы — или
  // нет, и её надо заполнить руками.
  'teacher_employment_created',
  'teacher_employment_skipped',
  // ТЗ 0079: `esign_setup --rerender-drafts` пересобрал черновик.
  'draft_rerendered',
  'finalize_waiting',
  // ТЗ 0077: SMS-напоминания — робот, оператор и ручные действия сотрудника.
  'sms_sent',
  'sms_failed',
  'sms_delivered',
  'sms_undelivered',
  'sms_reminder_requested',
  'sms_reminders_stopped',
  'sms_reminders_resumed',
  'link_recovered',
  // ТЗ 0078: продавец, короткая ссылка, письмо сотрудника из карточки.
  'seller_changed',
  'short_link_revealed',
  'email_written',
  // ТЗ 0078, этап 3: SMS со своим текстом (обычно из шаблона) из карточки.
  'sms_written',
  // ТЗ 0078, перепроверка: ответ сотрудника в треде договора.
  'email_replied',
  // Сверка с Documenso (`reconcile`): статус взят опросом, а не вебхуком.
  'poll_sent',
  'poll_opened',
  'poll_completed',
  'poll_rejected',
  'poll_cancelled',
  'poll_expired',
  // ТЗ 0080: Admin погасил ссылку на подпись на экране «Короткие ссылки».
  'short_link_revoked',
  // ТЗ 0094: письмо-приглашение поправлено перед отправкой (разово).
  'email_edited',
] as const;

/** События, которые у документа преподавателя называются по-своему. */
export const ESIGN_TEACHER_EVENT_OVERRIDES: readonly string[] = [
  'fulfilment_failed',
  'fulfilment_retry',
];

/**
 * Ключ перевода статуса выполнения: у студента это зачисление, у документа
 * преподавателя — создание его договора (перепроверка 0079).
 */
export const esignFulfilmentKey = (
  kind: string | null | undefined,
  status: string,
): string =>
  kind && ESIGN_TEACHER_KINDS.includes(kind)
    ? `esign.fulfilmentTeacher.${status}`
    : `esign.fulfilment.${status}`;

export const isKnownEsignEvent = (event: string): boolean =>
  (ESIGN_KNOWN_EVENTS as readonly string[]).includes(event);

/**
 * Подпись строки журнала: известное событие — своим переводом; прочие
 * события Documenso (`DOCUMENT_*`, `RECIPIENT_*` — провайдер может прислать
 * новое) — общей фразой с кодом; остальное — кодом как есть.
 */
export const esignEventLabel = (
  event: string,
  kind?: string | null,
): { key: string | null; params: Record<string, string> } => {
  // У документа преподавателя «выполнение» — это создание его договора, а
  // не зачисление на курс (перепроверка 0079).
  if (
    kind &&
    ESIGN_TEACHER_KINDS.includes(kind) &&
    ESIGN_TEACHER_EVENT_OVERRIDES.includes(event)
  ) {
    return { key: `esign.eventsTeacher.${event}`, params: {} };
  }
  if (isKnownEsignEvent(event))
    return { key: esignEventKey(event), params: {} };
  if (/^(DOCUMENT|RECIPIENT|ENVELOPE)_/.test(event)) {
    return { key: 'esign.events.providerOther', params: { code: event } };
  }
  return { key: null, params: { code: event } };
};

/** Почему приглашение не ушло (`emailError.code`, `email_skipped.reason`). */
export const ESIGN_EMAIL_ERROR_CODES = [
  'mail_disabled',
  'no_mailbox',
  'no_signing_url',
  'no_template_configured',
  'not_live',
  'failed',
  // ТЗ 0084: нет сократителя — длинной ссылкой письмо не уходит; шаблон
  // письма-приглашения выключен или удалён.
  'shortlink_disabled',
  'no_template',
] as const;

/** Почему копия подписанного договора не ушла (`signed_copy_skipped.reason`). */
export const ESIGN_SIGNED_COPY_REASONS = [
  'mail_disabled',
  'transport_disabled',
  'no_mailbox',
  'no_template',
  'no_signed_pdf',
  'sent_by_documenso',
  'failed',
] as const;

/**
 * Ключ перевода для события журнала. Точки в кодах бэкенда нет, но регистр
 * бывает верхний (события Documenso) — i18next это не смущает.
 */
export const esignEventKey = (event: string): string => `esign.events.${event}`;

/**
 * Коды ошибок записи, которые переводятся `esign.errors.<CODE>` (ТЗ 0078
 * добавил продавца, короткую ссылку и письма из ящика). Ответ на письмо в
 * тред (`sendGmailReply` коммуникатора) отдаёт коды строчными —
 * `esignErrorCode` приводит их к этому же виду.
 */
export const ESIGN_MAIL_ERROR_CODES = [
  'NO_MAILBOX',
  'SHORTLINK_DISABLED',
  'INVALID_SELLER',
  'SAME_SELLER',
  'REASON_REQUIRED',
  'NOT_YOUR_MAILBOX',
  'MAIL_ERROR',
  'GMAIL_ERROR',
  'REQUIRED',
  'DOES_NOT_EXIST',
  // ТЗ 0078, этап 3: письмо со своим текстом — подстановки заполняет сервер.
  'LINK_NOT_READY',
  'UNKNOWN_PLACEHOLDER',
  'TOO_LONG',
  // `{link}` — только в тексте письма, в теме запрещён.
  'LINK_IN_SUBJECT',
  // ТЗ 0083: переписку договора ведут продавец, аккаунт-менеджеры и Admin.
  'NOT_YOUR_CONTRACT',
] as const;

/**
 * ТЗ 0078, этап 4: отказы выбора шаблона договора студента. Переводятся
 * `esign.errors.<CODE>`; поле формы — «Шаблон договора» (`ESIGN_ERROR_FIELD`).
 */
export const ESIGN_TEMPLATE_ERROR_CODES = [
  'TEMPLATE_PLAN_MISMATCH',
  'TEMPLATE_UNAVAILABLE',
  // ТЗ 0079: выбранного лида нет в воронке New Leads.
  'CRM_LEAD_NOT_FOUND',
] as const;

/**
 * ТЗ 0079: предупреждения по реквизитам, которые подписант ввёл в Documenso.
 * Переводятся `esign.requisiteWarnings.<CODE>`.
 */
export const ESIGN_REQUISITE_WARNINGS = [
  'ZIP_FORMAT',
  'NIP_CHECKSUM',
  'COMPANY_WITHOUT_NIP',
  // 11 цифр с неверной контрольной; номер паспорта не проверяется.
  'PESEL_CHECKSUM',
  // Преподаватель, B2B (перепроверка 0079).
  'REGON_CHECKSUM',
] as const;

/**
 * ТЗ 0079: почему список лидов Kommo пуст (`crmLeads.error.code`).
 * Переводятся `esign.lead.errors.<CODE>` — форма при этом работает руками.
 */
export const ESIGN_CRM_ERROR_CODES = [
  'NOT_CONFIGURED',
  'DISABLED',
  'NETWORK',
  'AUTH',
  'RATE_LIMITED',
  'NO_PIPELINE',
  'ERROR',
] as const;

export const esignErrorCode = (code: string | null | undefined): string =>
  (code ?? '').trim().toUpperCase();

export interface IEsignEventDetail {
  /** Ключ перевода строки-пояснения под событием журнала. */
  key: string;
  params: Record<string, string>;
}

/**
 * Справочники для пояснений журнала (ТЗ 0078): в событии лежит только
 * ссылка на шаблон, имя берётся из справочника, если он загружен.
 *
 * * `messageTemplates` — id шаблона SMS/письма → название;
 * * `contractTemplates` — «код vN» шаблона договора → название.
 */
export interface IEsignEventLookups {
  messageTemplates?: Readonly<Record<string, string>>;
  contractTemplates?: Readonly<Record<string, string>>;
}

const detailId = (details: unknown, field: string): string => {
  if (!details || typeof details !== 'object') return '';
  const value = (details as Record<string, unknown>)[field];
  if (typeof value === 'number') return String(value);
  return typeof value === 'string' ? value.trim() : '';
};

const detailString = (details: unknown, field: string): string => {
  if (!details || typeof details !== 'object') return '';
  const value = (details as Record<string, unknown>)[field];
  return typeof value === 'string' ? value.trim() : '';
};

const detailFlag = (details: unknown, field: string): boolean =>
  Boolean(details) &&
  typeof details === 'object' &&
  (details as Record<string, unknown>)[field] === true;

/**
 * Пояснение к событию журнала из его `details` (ТЗ 0078).
 *
 * * `seller_changed` — «кто → кому» и причина;
 * * `email_written` — с какого ящика и с какой темой написал сотрудник;
 * * `email_sent` c `via='mailbox'` — приглашение ушло из ящика, а не через
 *   SendGrid.
 *
 * `null` — пояснять нечего (или бэкенд не прислал нужных полей): строка
 * журнала остаётся без второй строки, а не с пустыми кавычками.
 */
export const esignEventDetail = (
  event: string,
  details: unknown,
  lookups: IEsignEventLookups = {},
): IEsignEventDetail | null => {
  if (event === 'seller_changed') {
    const from = detailString(details, 'from_name') || '—';
    const to = detailString(details, 'to_name');
    const reason = detailString(details, 'reason');
    if (!to) return null;
    return reason
      ? {
          key: 'esign.eventDetails.sellerChanged',
          params: { from, to, reason },
        }
      : {
          key: 'esign.eventDetails.sellerChangedNoReason',
          params: { from, to },
        };
  }
  if (event === 'email_written') {
    const mailbox = detailString(details, 'mailbox');
    const subject = detailString(details, 'subject');
    if (!mailbox) return null;
    return subject
      ? { key: 'esign.eventDetails.emailWritten', params: { mailbox, subject } }
      : { key: 'esign.eventDetails.emailFrom', params: { mailbox } };
  }
  if (event === 'sms_written') {
    // Удалённый шаблон в справочнике уже не найти — тогда строка журнала
    // остаётся просто «SMS отправлено вручную», без выдуманного имени.
    const templateId = detailId(details, 'template_id');
    const name = templateId ? lookups.messageTemplates?.[templateId] : '';
    return name
      ? { key: 'esign.eventDetails.smsTemplate', params: { name } }
      : null;
  }
  if (event === 'draft_updated') {
    // Бэкенд пишет шаблон, только если правка его сменила: «код vN».
    const template = detailString(details, 'template');
    if (!template) return null;
    return {
      key: 'esign.eventDetails.templateChanged',
      params: { template: lookups.contractTemplates?.[template] || template },
    };
  }
  if (event === 'email_skipped' || event === 'email_failed') {
    const reason =
      detailString(details, 'reason') ||
      (event === 'email_failed' ? 'failed' : '');
    return (ESIGN_EMAIL_ERROR_CODES as ReadonlyArray<string>).includes(reason)
      ? { key: `esign.drawer.emailError.codes.${reason}`, params: {} }
      : null;
  }
  if (event === 'signed_copy_sent') {
    const mailbox = detailString(details, 'mailbox');
    return mailbox
      ? { key: 'esign.eventDetails.emailFrom', params: { mailbox } }
      : null;
  }
  if (event === 'signed_copy_skipped' || event === 'signed_copy_failed') {
    const reason =
      detailString(details, 'reason') ||
      (event === 'signed_copy_failed' ? 'failed' : '');
    return (ESIGN_SIGNED_COPY_REASONS as ReadonlyArray<string>).includes(reason)
      ? { key: `esign.signedCopy.reasons.${reason}`, params: {} }
      : null;
  }
  if (event === 'teacher_card_updated') {
    // В событии — только имена колонок, значений нет (ПДн).
    const record =
      details && typeof details === 'object'
        ? (details as Record<string, unknown>)
        : {};
    const written = Array.isArray(record.written) ? record.written.length : 0;
    const skipped =
      record.skipped && typeof record.skipped === 'object'
        ? (record.skipped as Record<string, unknown>)
        : {};
    if (skipped['*'] === 'no_employment_record') {
      return { key: 'esign.eventDetails.teacherCardNoRecord', params: {} };
    }
    return {
      key: 'esign.eventDetails.teacherCardWritten',
      params: { n: String(written) },
    };
  }
  if (event === 'teacher_employment_created') {
    // `model` — класс записи бэкенда; значения полей в событие не пишутся.
    const model = detailString(details, 'model');
    return (ESIGN_TEACHER_EMPLOYMENT_MODELS as readonly string[]).includes(
      model,
    )
      ? { key: `esign.eventDetails.teacherEmployment${model}`, params: {} }
      : null;
  }
  if (event === 'teacher_employment_skipped') {
    return {
      key: esignEmploymentSkipKey(detailString(details, 'reason')),
      params: {},
    };
  }
  if (event === 'email_replied') {
    const mailbox = detailString(details, 'mailbox');
    return mailbox
      ? { key: 'esign.eventDetails.emailFrom', params: { mailbox } }
      : null;
  }
  if (event === 'email_edited') {
    // ТЗ 0094: что поправили — тему, текст или оба; шаблон не меняется.
    const subject = detailFlag(details, 'subject_edited');
    const body = detailFlag(details, 'body_edited');
    if (subject && body) {
      return { key: 'esign.eventDetails.emailEditedBoth', params: {} };
    }
    if (subject) {
      return { key: 'esign.eventDetails.emailEditedSubject', params: {} };
    }
    if (body) return { key: 'esign.eventDetails.emailEditedBody', params: {} };
    return null;
  }
  if (event === 'send_requested' && detailFlag(details, 'email_edited')) {
    return { key: 'esign.eventDetails.sendEmailEdited', params: {} };
  }
  if (event === 'email_sent' && detailString(details, 'via') === 'mailbox') {
    const mailbox = detailString(details, 'mailbox');
    const edited = detailFlag(details, 'edited');
    if (!mailbox) {
      return edited
        ? { key: 'esign.eventDetails.emailSentEdited', params: {} }
        : null;
    }
    return edited
      ? { key: 'esign.eventDetails.emailFromEdited', params: { mailbox } }
      : { key: 'esign.eventDetails.emailFrom', params: { mailbox } };
  }
  return null;
};

/**
 * Карточка занятости НЕ заведена, и это последнее слово журнала: из двух
 * событий (`teacher_employment_created` / `_skipped`) берётся позднейшее —
 * после «Повторить» запись могла и появиться. `null` — предупреждать не о
 * чем. `key` — причина на языке интерфейса (`esignEmploymentSkipKey`).
 */
export const esignTeacherEmploymentSkip = (
  events: ReadonlyArray<{ event: string; details?: unknown }>,
): { reason: string; key: string } | null => {
  for (let i = events.length - 1; i >= 0; i -= 1) {
    const item = events[i];
    if (item.event === 'teacher_employment_created') return null;
    if (item.event === 'teacher_employment_skipped') {
      const reason = detailString(item.details, 'reason');
      return { reason, key: esignEmploymentSkipKey(reason) };
    }
  }
  return null;
};

/** «Имя Фамилия» подписанта. */
export const esignSignerName = (row: {
  signerFirstName?: string | null;
  signerLastName?: string | null;
}): string =>
  [row.signerFirstName, row.signerLastName].filter(Boolean).join(' ') || '—';

/** Деньги приходят строками «6500.00» — в копейках, чтобы не терять грош. */
const toCents = (value: string | number | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const normalized = String(value).replace(',', '.').trim();
  if (!/^\d+(\.\d{0,2})?$/.test(normalized)) return null;
  const [whole, fraction = ''] = normalized.split('.');
  return Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
};

const fromCents = (cents: number): string =>
  `${Math.floor(cents / 100)}.${String(cents % 100).padStart(2, '0')}`;

/**
 * Разбор суммы, набранной в поле цены. `null` — не число или не больше нуля.
 * Возвращает строку «6500.00» — в таком виде её ждёт бэкенд.
 */
export const normalizeEsignPrice = (
  value: string | null | undefined,
): string | null => {
  const cents = toCents(value);
  if (cents === null || cents <= 0) return null;
  return fromCents(cents);
};

/**
 * Итог договора с фирмой: цена за участника × число участников. Та же
 * арифметика в копейках, что у бэкенда (`company_context.total_price`).
 */
export const esignCompanyTotal = (
  pricePerPerson: string | null | undefined,
  participants: number,
): string | null => {
  const cents = toCents(pricePerPerson);
  if (cents === null || cents <= 0 || participants <= 0) return null;
  return fromCents(cents * participants);
};

const NIP_WEIGHTS = [6, 5, 7, 2, 3, 4, 5, 6, 7];

/**
 * NIP: 10 цифр с контрольной (как `finalize.nip_is_valid` бэкенда). Дефисы и
 * пробелы допустимы — бэкенд сам оставит одни цифры.
 */
export const esignNipValid = (value: string | null | undefined): boolean => {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (digits.length !== 10) return false;
  const sum = NIP_WEIGHTS.reduce(
    (acc, weight, index) => acc + weight * Number(digits[index]),
    0,
  );
  return sum % 11 === Number(digits[9]);
};

/** REGON: пусто, 9 или 14 цифр. */
export const esignRegonValid = (value: string | null | undefined): boolean => {
  const digits = String(value ?? '').replace(/\D/g, '');
  return digits.length === 0 || digits.length === 9 || digits.length === 14;
};

/** Скидка против прайса: `null`, если цена не ниже прайса или не число. */
export const esignDiscount = (
  price: string | null | undefined,
  listPrice: string | null | undefined,
): string | null => {
  const priceCents = toCents(price);
  const listCents = toCents(listPrice);
  if (priceCents === null || listCents === null) return null;
  if (priceCents >= listCents) return null;
  return fromCents(listCents - priceCents);
};

export interface IEsignInstallmentPreview {
  n: number;
  amount: string;
  rule: string;
  ruleKind?: string | null;
  ruleParams?: unknown;
}

/**
 * Коды правила срока взноса (ТЗ 0078, `ContractInstallmentType.ruleKind`).
 * `rule` — польская фраза из текста договора; интерфейс показывает правило на
 * своём языке, а фразу — только если кода нет (старый сервер).
 */
export const ESIGN_RULE_KINDS = [
  'group_start',
  'day_of_month_first',
  'days_after_signing',
  'day_of_month_next',
  'day_of_month_every',
  'months_after_first',
] as const;

export interface IEsignRuleLabel {
  /** Ключ i18n или `null` — тогда показывается `fallback`. */
  key: string | null;
  params: Record<string, string | number>;
  fallback: string;
}

export const esignRuleLabel = (row: {
  rule: string;
  ruleKind?: string | null;
  ruleParams?: unknown;
}): IEsignRuleLabel => {
  const kind = row.ruleKind ?? '';
  const known = (ESIGN_RULE_KINDS as ReadonlyArray<string>).includes(kind);
  const raw =
    row.ruleParams && typeof row.ruleParams === 'object'
      ? (row.ruleParams as Record<string, unknown>)
      : {};
  const params: Record<string, string | number> = {};
  Object.entries(raw).forEach(([name, value]) => {
    if (typeof value === 'string' || typeof value === 'number') {
      params[name] = value;
    }
  });
  return {
    key: known ? `esign.scheduleRule.${kind}` : null,
    params,
    fallback: row.rule || '—',
  };
};

/**
 * Превью графика под ИЗМЕНЁННУЮ цену.
 *
 * Бэкенд считает график от прайса (`contractDraftDefaults.schedule`), а
 * продажник может поправить сумму. Бэкенд при создании черновика делит цену
 * так же — поровну, остаток округления в последний взнос, — поэтому превью
 * пересчитывается здесь, а правила дат берутся из ответа бэкенда как есть.
 *
 * Прайса для валюты у группы может не быть — тогда бэкенд графика не
 * присылает вовсе, и строки строятся по числу взносов тарифа без правила.
 */
export const esignSchedulePreview = (
  price: string | null | undefined,
  base: ReadonlyArray<IEsignInstallmentPreview>,
  installments = 0,
): IEsignInstallmentPreview[] => {
  const cents = toCents(price);
  const rows: Array<Omit<IEsignInstallmentPreview, 'amount'>> =
    base.length > 0
      ? base.map((row) => ({
          n: row.n,
          rule: row.rule,
          ruleKind: row.ruleKind,
          ruleParams: row.ruleParams,
        }))
      : Array.from({ length: Math.max(installments, 0) }, (_, index) => ({
          n: index + 1,
          rule: '',
        }));
  if (cents === null || cents <= 0 || rows.length === 0) {
    return base.map((row) => ({ ...row }));
  }
  const count = rows.length;
  const part = Math.floor(cents / count);
  return rows.map((row, index) => ({
    ...row,
    amount: fromCents(index === count - 1 ? cents - part * (count - 1) : part),
  }));
};

/**
 * Коды предупреждений `contractDraftDefaults.warnings`.
 *
 * `KNOWN_PERSON` — человек есть в системе, но не в юрлице продажника: имя
 * бэкенд тогда намеренно НЕ отдаёт (иначе поиск по email стал бы справочником
 * чужих клиентов), поэтому и подсказка нейтральная, без имени.
 */
export const ESIGN_DRAFT_WARNINGS = [
  'GROUP_HAS_NO_FUTURE_LESSONS',
  'DUPLICATE_CONTRACT',
  'NUMBER_TOO_LONG',
  'AMBIGUOUS_PERSON',
  'KNOWN_PERSON',
  // Переходный период: у человека уже есть PandaDoc-документ в папке этой
  // группы. Не запрет — старый документ мог истечь или потеряться.
  'PANDADOC_IN_FLIGHT',
  'SCHOOL_PROFILE_MISSING',
  // ТЗ 0079: по выбранному лиду Kommo уже есть договор — не запрет.
  'CRM_LEAD_USED',
] as const;

/**
 * Предупреждения, с которыми бэкенд черновик НЕ создаст — кнопка «Создать»
 * тогда выключена, и человек видит причину заранее, а не после клика.
 */
export const ESIGN_BLOCKING_WARNINGS: readonly string[] = [
  'GROUP_HAS_NO_FUTURE_LESSONS',
  'DUPLICATE_CONTRACT',
  'NUMBER_TOO_LONG',
  // Без реквизитов школы (NIP, REGON, представитель) текст договора не
  // собирается — бэкенд черновик не создаст.
  'SCHOOL_PROFILE_MISSING',
];

/** ТЗ 0079: то, что форма договора берёт из лида Kommo. */
export interface IEsignLeadPerson {
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

/** Имя человека из контакта лида; нет контакта — название лида. */
export const esignLeadPersonName = (lead: IEsignLeadPerson): string =>
  [lead.firstName, lead.lastName].filter(Boolean).join(' ') || lead.name;

/**
 * Поля подписанта из лида — все четыре, даже пустые: выбор другого лида
 * не должен оставить email или телефон предыдущего человека.
 */
export const esignLeadSignerFields = (lead: IEsignLeadPerson) => ({
  firstName: lead.firstName.trim(),
  lastName: lead.lastName.trim(),
  email: lead.email.trim(),
  phone: lead.phone.trim(),
});

/** Код ошибки мутации → поле формы «Новый договор». */
export const ESIGN_ERROR_FIELD: Readonly<Record<string, string>> = {
  INVALID_EMAIL: 'email',
  INVALID_PHONE: 'phone',
  INVALID_PRICE: 'price',
  INVALID_NAME: 'firstName',
  PLAN_UNAVAILABLE: 'paymentPlanId',
  GROUP_HAS_NO_FUTURE_LESSONS: 'groupId',
  DUPLICATE_CONTRACT: 'groupId',
  NUMBER_TOO_LONG: 'groupId',
  // Договор с фирмой: бэкенд обычно сам называет поле, это — запасной путь.
  INVALID_TAX_ID: 'taxId',
  INVALID_COMPANY: 'companyName',
  NO_PARTICIPANTS: 'participants',
  INVALID_PAYMENT_MODEL: 'paymentModel',
  // ТЗ 0078: бэкенд называет поле `template` — у формы это `templateId`.
  TEMPLATE_PLAN_MISMATCH: 'templateId',
  TEMPLATE_UNAVAILABLE: 'templateId',
  CRM_LEAD_NOT_FOUND: 'crmLeadId',
};

/**
 * Подробности ошибки выполнения, которые сервер кладёт отдельными полями
 * (перепроверка 0079): интерфейс показывает их на своём языке, а русский
 * текст сервера — только для непереведённых кодов.
 */
export const ESIGN_FULFILMENT_ERROR_DETAILS = ['agreement', 'number'] as const;

/** `{code, message, details}` из `fulfilmentError` без догадок о форме. */
export const esignFulfilmentErrorInfo = (
  error: unknown,
): {
  code: string;
  message: string;
  details: Partial<
    Record<typeof ESIGN_FULFILMENT_ERROR_DETAILS[number], string>
  >;
} | null => {
  if (!error || typeof error !== 'object') return null;
  const record = error as Record<string, unknown>;
  const { code, message } = record;
  if (!code && !message) return null;
  const details: Partial<
    Record<typeof ESIGN_FULFILMENT_ERROR_DETAILS[number], string>
  > = {};
  ESIGN_FULFILMENT_ERROR_DETAILS.forEach((key) => {
    const value = record[key];
    if (typeof value === 'string' && value) details[key] = value;
  });
  return {
    code: typeof code === 'string' ? code : '',
    message: typeof message === 'string' ? message : '',
    details,
  };
};

/** Согласия подписанта: `{consent_terms: true, …}` → пары для списка. */
export const esignConsentList = (
  consents: unknown,
): Array<{ key: string; given: boolean }> => {
  if (!consents || typeof consents !== 'object') return [];
  return Object.entries(
    consents as Record<string, unknown>,
  ).map(([key, value]) => ({ key, given: Boolean(value) }));
};

/**
 * ТЗ 0090 — «Новый договор» из карточки лида (`contractDraftFromLead`).
 * Словари — для перевода (`esign.fromLead.*`) и локаль-гейта: откуда курс,
 * почему подобран тариф, почему связи с лидом нет.
 */
export const ESIGN_FROM_LEAD_COURSE_SOURCES = [
  'funding_candidate',
  'kommo_course',
] as const;

export const ESIGN_FROM_LEAD_PLAN_REASONS = ['funding_candidate'] as const;

export const ESIGN_FROM_LEAD_BLOCKS = [
  'LEAD_LEFT_PIPELINE',
  'LEAD_DELETED',
  'SOURCE_DISABLED',
] as const;

/**
 * Параметры адреса, каждый из которых открывает своё окно раздела. Открыто
 * ровно одно (правило раздела дофинансирования, спека 0055).
 */
export const CONTRACT_SURFACE_PARAMS = [
  'contract',
  'contractNew',
  'contractEdit',
  'companyNew',
  'companyEdit',
  // Окно «Договор с преподавателем» (рамочный договор).
  'teacherNew',
  'group',
  // ТЗ 0090: «Новый договор» из карточки лида — принадлежит окну нового
  // договора и уходит вместе с ним.
  'fromLead',
];

/**
 * Открыть окно раздела, СОХРАНИВ остальные параметры (фильтры списка живут
 * в адресе): палитра команд, вызванная на самой странице договоров, иначе
 * стирала фильтры вместе с прошлым окном.
 */
export const withContractSurface = (
  currentSearch: string,
  patch: Record<string, string | null>,
): string => {
  const params = new URLSearchParams(currentSearch);
  CONTRACT_SURFACE_PARAMS.forEach((key) => params.delete(key));
  Object.entries(patch).forEach(([key, value]) => {
    if (value !== null) params.set(key, value);
  });
  return params.toString();
};
